import {
  PREVIEW_REFERENCE,
  PREVIEW_TO_EMAIL,
} from "@/features/material-compliance/components/supplier-declarations/inquiry/settings/templateSettingsMock"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type { ContentLanguage } from "../components/SettingsControls"

/** The sample supplier in the language of the copy, as the EN and DE
 *  catalogs name it (MC_TPL_PREVIEW_SUPPLIER_NAME). */
const SAMPLE_SUPPLIER: Record<ContentLanguage, string> = {
  en: "Supplier",
  de: "Lieferant",
}

/** Sample regulations, named as the backend names asked ones (regulations_phrase). */
const SAMPLE_REGULATIONS: Record<ContentLanguage, string> = {
  en: "REACH SVHC and RoHS",
  de: "REACH-SVHC und RoHS",
}

/**
 * Fills a template's {{variables}} with sample values, so the preview reads
 * like a real email: in the language of the copy, as the backend fills them
 * on send. The EN and DE token names both resolve.
 */
export function resolvePreviewVariables(
  text: string,
  language: ContentLanguage,
): string {
  const sampleDeadline = new Date(Date.UTC(2026, 7, 4)).toLocaleDateString(
    language === "de" ? "de-DE" : "en-US",
    { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" },
  )
  return text
    .replace(
      /\{\{\s*(supplier_name|lieferant)\s*\}\}/g,
      SAMPLE_SUPPLIER[language],
    )
    .replace(/\{\{\s*(deadline|frist)\s*\}\}/g, sampleDeadline)
    .replace(/\{\{\s*(reference|referenz)\s*\}\}/g, PREVIEW_REFERENCE)
    .replace(
      /\{\{\s*(regulations|regelwerke)\s*\}\}/g,
      SAMPLE_REGULATIONS[language],
    )
}

interface EmailPreviewProps {
  footer: string
  // When provided (Email template section), the real subject/body are shown with sample
  // {{variables}} resolved; otherwise the subject is a generic placeholder and the body is
  // decorative lines (the signature-only section, which doesn't edit the message).
  subject?: string
  body?: string
}

// Width fractions for the placeholder body lines (purely decorative).
const BODY_LINES = ["w-full", "w-11/12", "w-full", "w-4/5", "w-2/3"]

/** Preview of the outgoing email — footer (signature) is always live; subject/body are
 *  live when supplied, else placeholders. */
export function EmailPreview({ footer, subject, body }: EmailPreviewProps) {
  const { translate } = useTranslation()

  return (
    <div className="rounded-lg bg-white text-[12.5px] text-[#222] shadow-lg">
      <div className="space-y-1.5 border-b border-[#ececec] px-5 py-4">
        <div className="flex gap-2">
          <span className="w-14 shrink-0 text-[11px] font-medium text-[#999]">
            {translate("MC_TPL_EMAIL_TO")}
          </span>
          <span className="text-[12px] text-[#333]">{PREVIEW_TO_EMAIL}</span>
        </div>
        <div className="flex gap-2">
          <span className="w-14 shrink-0 text-[11px] font-medium text-[#999]">
            {translate("MC_TPL_EMAIL_SUBJECT")}
          </span>
          <span className="text-[12px] font-medium text-[#222]">
            {subject && subject.trim().length > 0
              ? subject
              : translate("MC_TPL_PREVIEW_SUBJECT")}
          </span>
        </div>
      </div>

      {body !== undefined ? (
        <div className="px-5 py-5 text-[12.5px] leading-relaxed whitespace-pre-line text-[#333]">
          {body}
        </div>
      ) : (
        <div className="space-y-2.5 px-5 py-5">
          {BODY_LINES.map((w, i) => (
            <span key={i} className={`block h-2.5 rounded bg-[#ededed] ${w}`} />
          ))}
        </div>
      )}

      <div className="border-t border-[#ececec] px-5 py-4 text-[12.5px] whitespace-pre-line text-[#333]">
        {footer}
      </div>
    </div>
  )
}
